"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Search, ShoppingBag, Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden lg:flex gap-1">
        <div className="h-9 w-20 bg-primary/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-primary/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-primary/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-primary/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-primary/30 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-foreground hover:!bg-transparent focus:!bg-transparent transition-all duration-[var(--duration-medium)] group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] origin-left" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
        {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b border-border lg:hidden shadow-medium">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-2">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-sm font-medium text-foreground hover:bg-primary/20 rounded-md transition-all duration-[var(--duration-fast)]" onClick={() => setIsMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
function SearchOverlayResults() {
  const {
    products,
    loading,
    query
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] bg-muted animate-pulse rounded-lg" />)}
      </div>;
  }
  if (products.length === 0 && query.search) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground">No encontramos resultados, prueba otra búsqueda</p>
      </div>;
  }
  if (products.length === 0) return null;
  return <div className="mt-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {products.map((product, i) => <div key={product.id} data-index={i} className="group flex flex-col">
            <Link to={product.link} className="relative aspect-[4/5] overflow-hidden rounded-lg mb-3 shadow-soft">
              <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </Link>
            <Link to={product.link} className="font-medium text-sm mb-1 hover:text-accent transition-colors line-clamp-1">
              {product.title}
            </Link>
            <p className="text-sm font-semibold mb-3">${product.price}</p>
            <Link to={product.link}>
              <Button variant="outline" size="sm" className="w-full mb-2">
                Ver producto
              </Button>
            </Link>
            <AddToCartButton productId={product.id} text="Añadir al carrito" size="sm" buttonClassName="bg-primary text-foreground-primary" className="w-full" />
          </div>)}
      </div>
      <div className="mt-8 text-center">
        <Link to={`/?s=${query.search}&post_type=product`} className="text-sm font-medium text-accent hover:underline transition-all">
          Ver todos los resultados
        </Link>
      </div>
    </div>;
}
function SearchOverlay({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] bg-background/98 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="container mx-auto px-6 pt-20">
        <div className="flex justify-end mb-8">
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close search">
            <X className="size-6" />
          </Button>
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
            <Input type="search" placeholder="¿Qué estás buscando?" autoFocus className="w-full h-14 pl-12 text-xl bg-transparent border-b border-t-0 border-x-0 rounded-none focus-visible:ring-0 focus-visible:border-accent transition-all" value={query} onChange={e => setQuery(e.target.value)} />
          </div>

          <EcommerceProductsProvider postType="product" wp_query={{
          search: query,
          per_page: 8
        }} fields={["id", "title.rendered", "link", "featured_image_url", "price"]}>
            <SearchOverlayResults />
          </EcommerceProductsProvider>
        </div>
      </div>
    </div>;
}
function SearchResultsConsumer() {
  const {
    products,
    loading
  } = useEcommerceProducts();
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSearchQuery(params.get("s") || "");
  }, []);
  if (!searchQuery) return null;
  return <div className="bg-primary-light/30 border-b border-border py-8 animate-fade-in-up">
      <div className="container mx-auto px-6">
        <h2 className="text-2xl font-display mb-6">Resultados para: "{searchQuery}"</h2>
        {loading ? <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] bg-muted animate-pulse rounded-lg" />)}
          </div> : products.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((product, i) => <div key={product.id} data-index={i} className="group flex flex-col">
                <Link to={product.link} className="relative aspect-[4/5] overflow-hidden rounded-lg mb-3 shadow-soft">
                  <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </Link>
                <Link to={product.link} className="font-medium text-sm mb-1 hover:text-accent transition-colors">
                  {product.title}
                </Link>
                <p className="text-sm font-semibold mb-3">${product.price}</p>
                <AddToCartButton productId={product.id} text="Añadir al carrito" size="sm" buttonClassName="bg-primary text-foreground-primary" className="w-full" />
              </div>)}
          </div> : <p className="text-muted-foreground">No se encontraron productos que coincidan con tu búsqueda.</p>}
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const cartItemCount = 0;
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  const searchParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get("s") || "" : "";
  return <>
      <header id="header" className={`w-full sticky top-0 z-50 border-b border-transparent transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isScrolled ? "h-16 backdrop-blur-md bg-background/90 border-border/50 shadow-soft" : "h-[90px] bg-background"}`}>
        <div className="container mx-auto px-6 h-full flex items-center justify-between gap-8">
          <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-opacity duration-[var(--duration-fast)]">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1548683/img/lumiere_boutique_logo.png" alt="LUMIÈRE Boutique" className="h-7" />
          </Link>

          <MenuProvider menu_id="53" className="flex-1 flex justify-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent hover:text-accent transition-colors duration-[var(--duration-fast)]" aria-label="Search" onClick={() => setIsSearchOpen(true)}>
              <Search className="size-5" />
            </Button>

            <Link to="/CartPage">
              <Button variant="ghost" size="icon" className="relative hover:!bg-transparent focus:!bg-transparent hover:text-accent transition-colors duration-[var(--duration-fast)]" aria-label="Shopping cart">
                <ShoppingBag className="size-5" />
                {cartItemCount > 0 && <span className="absolute -top-1 -right-1 flex items-center justify-center size-4 text-[9px] font-bold bg-white text-accent border border-accent/20 rounded-full shadow-soft">
                    {cartItemCount}
                  </span>}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <EcommerceProductsProvider postType="product" wp_query={{
      search: searchParam,
      per_page: 4
    }} fields={["id", "title.rendered", "link", "featured_image_url", "price"]}>
        <SearchResultsConsumer />
      </EcommerceProductsProvider>
    </>;
}